import * as RadixTooltip from "@radix-ui/react-tooltip";
import { type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "../overlayPositioning";

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  variant?: "default" | "white";
  delayDuration?: number;
  /** When true, the tooltip never opens but the trigger stays mounted unchanged. */
  disabled?: boolean;
};

type TooltipVariant = "default" | "white";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 * Net line-height: bfrs-leading-5 [pos 49092] wins over the line-height of
 * bfrs-text-xs [pos 48728] (later in the compiled stylesheet). */

const tooltipVariantStyles: Record<TooltipVariant, CSSObject> = {
  default: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-inverse) / var(--tw-text-opacity, 1))"
  },
  white: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "rgb(255 255 255 / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

const tooltipArrowVariantStyles: Record<TooltipVariant, CSSObject> = {
  default: { fill: "hsl(var(--bfrs-color-text-primary) / 1)" },
  white: { fill: "#fff" }
};

const TooltipContent = styled(RadixTooltip.Content, {
  label: "AgenticTooltip-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: TooltipVariant } }>(({ ownerState }) => [
  {
    zIndex: 1000,
    borderRadius: "var(--bfrs-radius-md)",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    fontSize: ".75rem",
    lineHeight: "1.25rem",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  tooltipVariantStyles[ownerState.variant],
  { maxWidth: "min(16rem,var(--radix-tooltip-content-available-width))" }
]);

const TooltipArrow = styled(RadixTooltip.Arrow, {
  label: "AgenticTooltip-arrow",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { variant: TooltipVariant } }>(({ ownerState }) => tooltipArrowVariantStyles[ownerState.variant]);

export function Tooltip({ content, children, side = "top", variant = "default", delayDuration = 350, disabled = false }: TooltipProps) {
  const resolvedVariant = variant === "white" ? "white" : "default";

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root open={disabled ? false : undefined}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <BfrsPortalScope>
            <TooltipContent
              side={side}
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              ownerState={{ variant: resolvedVariant }}
            >
              {content}
              <TooltipArrow ownerState={{ variant: resolvedVariant }} />
            </TooltipContent>
          </BfrsPortalScope>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
